/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo } from 'react';
import { TimelineEventItem, TimelineEventItemTypeEnum } from 'types/Timeline';
import { dateFormat } from 'utils/dateFormat';
import { ButtonProps } from 'components/_new/Button';
import { Icon } from 'components/_new/Icon';
import { classNames } from 'primereact/utils';
import { Link } from 'react-router-dom';
import { ApplicationRole } from 'types/Application';
import ExtendedBox from '../../userPanel/sessions/partials/ExtendedBox';
import { AplicationRoleVisibilityTag, MandatoryTag, RequireConfirmationTag } from './misc/TimelineComponents';
import { TimelineEventCardActions } from './misc/TimelineEventCardActions';

type TimelineEventCardProps = {
	data: TimelineEventItem;
	onTitleClick: (data: TimelineEventItem) => void;
	onUpdate?: (data: TimelineEventItem) => void;
	actions?: Array<ButtonProps & { key: string }>;
	additionalInfo?: boolean;
};

export const TimelineEventCard = ({
	data,
	onTitleClick,
	onUpdate,
	actions,
	additionalInfo = false,
}: TimelineEventCardProps) => {
	const flagcolor = useMemo(() => {
		switch (data.type) {
			case TimelineEventItemTypeEnum.EVENT:
				return '#f674cb';
			case TimelineEventItemTypeEnum.MILESTONE:
				return '#435cd8';
			case TimelineEventItemTypeEnum.SESSION:
				return 'gray';
			default:
				return 'gray';
		}
	}, [data.type]);

	const externalLink = data?.session?.link;
	const displayLimitInfo = Boolean(
		data.requireConfirmation &&
		data.participantLimit &&
		data.currentParticipants &&
		data.currentParticipants / data.participantLimit >= 0.5
	);
	const finalParticipantLimit = data ? Math.max(data.currentParticipants || 0, data.participantLimit || 0) : 0;

	return (
		<ExtendedBox flagcolor={flagcolor}>
			<div className="flex flex-column gap-2 justify-content-center w-full py-4 pr-4">
				<Link
					to={externalLink || '#'}
					target="_blank"
					className="flex flex-column gap-2 justify-content-between "
					onClick={(event) => {
						if (!externalLink) {
							onTitleClick(data);
							event.preventDefault();
						}
					}}
				>
					{additionalInfo ? (
						<>
							{(data.mandatory || data.requireConfirmation || data.forApplicationRole) && (
								<div className="flex flex-row gap-2 flex-wrap justify-content-start align-items-center">
									{data.mandatory && <MandatoryTag />}
									{data.requireConfirmation && <RequireConfirmationTag />}
									{data.forApplicationRole && (
										<AplicationRoleVisibilityTag
											role={data.forApplicationRole as ApplicationRole}
										/>
									)}
								</div>
							)}
						</>
					) : (
						<>
							{(data.mandatory || data.requireConfirmation) && (
								<div className="flex flex-row gap-2 flex-wrap justify-content-start align-items-center">
									{data.mandatory && <MandatoryTag />}
									{data.requireConfirmation && <RequireConfirmationTag />}
								</div>
							)}
						</>
					)}

					<div className="flex flex-row gap-2 justify-content-between align-items-center flex-wrap">
						<div className="flex flex-column gap-0">
							<div className="flex flex-row gap-2 align-items-start ">
								<span
									className={classNames('strong text-lg', {
										'text-primary': data.type !== TimelineEventItemTypeEnum.SESSION,
										'text-gray': data.type === TimelineEventItemTypeEnum.SESSION,
									})}
								>
									{data.summary}
								</span>
								{externalLink && <Icon name="external-link-alt" className="ml-1" />}
							</div>
						</div>

						<div className="flex flex-row flex-wrap gap-3 flex-nowrap bg-new-gray rounded-md px-2 py-1">
							<div className="flex flex-row gap-1 justify-content-center align-items-center">
								<Icon name="calendar" iconStyle="regular" className="text-purple" />
								<span className="text-nowrap">
									{dateFormat(data.startAt || data.dayDate, 'dayAndMonth')}
								</span>
							</div>
							{data.startAt && data.type !== TimelineEventItemTypeEnum.MILESTONE && (
								<div className="flex flex-row flex-nowrap gap-1 justify-content-center align-items-center">
									<Icon name="clock" iconStyle="regular" className="text-purple" />
									<span className="nowrap">{`${dateFormat(data.startAt, 'hours')}`}</span>
									{data.endAt && (
										<span className="nowrap">{` - ${dateFormat(data.endAt, 'hours')}`}</span>
									)}
								</div>
							)}

							{displayLimitInfo && (
								<div className="flex flex-row gap-1 justify-content-center align-items-center">
									<Icon name="users" iconStyle="solid" className="text-purple" />
									<span className="text-nowrap">
										{data.currentParticipants}/{finalParticipantLimit}
									</span>
								</div>
							)}
						</div>
					</div>
				</Link>

				{data.intro && data.intro.length > 0 && (
					<div className="flex flex-row align-items-center gap-2 mt-3 xl:mt-0">
						<div>{data.intro}</div>
					</div>
				)}

				<TimelineEventCardActions actions={actions} data={data} onUpdate={onUpdate} />
			</div>
		</ExtendedBox>
	);
};
